<template>
    <common-modal height="medium" width="large" @emitEvent="emitEvent($event, ['close'])" @close="emitEvent({ name: 'close' })">
        <div class="content">
            <div class="patient-details">
                <common-content-spacer height="medium">
                    <common-page-header :heading="patientFullName"></common-page-header>
                    <div class="patient-details-wrapper">
                        <div class="patient-details-wrapper--item">
                            <p>{{ $t('pages.referrals.patient_details.date_of_birth') }} {{ referral.patient.dateOfBirth }}</p>
                            <p class="patient-details-wrapper--item__info-icon">
                                <common-icon name="phone" size="small"></common-icon>
                                {{ referral.patient.mobilePhone }}
                            </p>
                        </div>
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--location">
                            <p v-if="$route.params.tabType !== 'pool'" class="patient-details-wrapper--item--location__icon">
                                <common-icon name="location" size="huge"></common-icon>
                            </p>
                            <div v-if="$route.params.tabType !== 'pool' && referral.patient?.address">
                                <p>
                                    {{ referral.patient.address }}
                                </p>
                                <p>
                                    {{ referral.patient.zip }} {{ referral.patient.city }},
                                    {{ referral.patient.state }}
                                </p>
                            </div>
                            <div v-else>
                                <p>N/A</p>
                            </div>
                        </div>
                    </div>
                    <div class="patient-details-wrapper">
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--insurance">
                            <common-icon name="insurance" size="medium"></common-icon>
                            <div class="patient-details-wrapper--item--insurance__info">
                                <p>
                                    <span class="patient-details-wrapper--item__heading">
                                        {{ $t('pages.referrals.patient_details.insurance') }}
                                    </span>
                                </p>
                                <p>
                                    {{ referral.insurancePlan?.name }}
                                </p>
                                <p>{{ $t('pages.referrals.patient_details.group') }} {{ referral.insuranceGroupNumber }}</p>
                                <p>{{ $t('pages.referrals.patient_details.id') }} {{ referral.insuranceNumber }}</p>
                            </div>
                        </div>
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--insurance">
                            <common-user-avatar
                                v-if="$route.params.tabType !== 'pool'"
                                :user="referral.primaryCarePhysician"
                                size="label"
                            ></common-user-avatar>
                            <div v-if="$route.params.tabType !== 'pool'" class="patient-details-wrapper--item--insurance__info">
                                <p class="patient-details-wrapper--item--avatar">
                                    <span class="patient-details-wrapper--item__heading">
                                        {{ $t('pages.referrals.patient_details.primary_care') }}
                                    </span>
                                </p>
                                <div v-if="referral.primaryCarePhysician?.id">
                                    <p>{{ referral.primaryCarePhysician?.name }}</p>
                                    <p class="patient-details-wrapper--item__info-icon">
                                        <common-icon name="email" size="small"></common-icon>
                                        {{ referral.primaryCarePhysician?.email }}
                                    </p>
                                    <p class="patient-details-wrapper--item__info-icon">
                                        <common-icon name="phone" size="small"></common-icon>
                                        {{ referral.primaryCarePhysician?.phone }}
                                    </p>
                                </div>
                                <div v-else>
                                    <p>N/A</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </common-content-spacer>
            </div>
            <div>
                <span class="patient-details-wrapper--item__heading">
                    {{ $t('pages.referrals.patient_details.clinical_history') }}
                </span>
            </div>
            <div class="patient-details-wrapper patient-details-wrapper--history">
                <div
                    class="patient-details-wrapper--item patient-details-wrapper--item__full-width patient-details-wrapper--item--insurance"
                >
                    <common-icon name="calendar" size="medium"></common-icon>
                    <div class="patient-details-wrapper--item--insurance__info">
                        <p>
                            <span class="patient-details-wrapper--item__heading">{{ appointmentDateTime }}</span>
                        </p>
                        <p>
                            {{ referral.diagnosis }}
                        </p>
                        <p v-if="referral.referralNotes && referral.referralNotes.length">
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.notes') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images
                                    ref="images"
                                    :images="referral.referralNotes"
                                    :showMoreOption="true"
                                ></common-images>
                            </common-summary-item>
                        </p>
                        <p v-if="referral.consultationReports && referral.consultationReports.length">
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.consultation_report') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images :images="referral.consultationReports" :showMoreOption="true"></common-images>
                            </common-summary-item>
                        </p>
                        <p
                            v-if="referral.attachments && referral.attachments.length"
                            class="patient-details-wrapper--attachments"
                        >
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.message_attachments') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images :images="referral.attachments" :showMoreOption="true"></common-images>
                            </common-summary-item>
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="referral.note" class="patient-details-wrapper--notes">
                <span class="patient-details-wrapper--item__heading">{{ $t('pages.referrals.patient_details.note') }}</span>
                <common-quote
                    :class="referral.shouldShowPatientInfo ? '' : 'text-blured'"
                    :content="referral.note"
                    :author="referringPhysicianName"
                ></common-quote>
            </div>
            <div v-if="userId && !isSelfReferred" @click="redirectToChatModal()">
                <common-icon size="gigantic" name="msg"></common-icon>
                <span class="referral-show-details__send-text">
                    {{ referringPhysicianNameLabel }}
                </span>
            </div>
            <div class="patient-details__upload-container">
                <p v-if="selectedFiles.length" class="patient-details__upload-container__title">Files to be added:</p>
                <input
                    ref="fileInput"
                    type="file"
                    multiple="multiple"
                    style="display: none"
                    accept="application/pdf,image/png,image/jpg,image/jpeg"
                    @change="fileSelected($event)"
                />
                <div class="patient-details__upload-section">
                    <common-button
                        v-if="!selectedFiles || !selectedFiles.length"
                        type="primary"
                        size="large"
                        :label="$t('modals.patient_record.add.upload_records')"
                        class="patient-details__upload-btn"
                        @click="$refs.fileInput.click()"
                    ></common-button>
                    <p v-if="!selectedFiles || !selectedFiles.length">{{ $t('pages.referrals.notes.add.file_limits') }}</p>

                    <common-upload-summary
                        v-else
                        class="patient-details__upload-summary"
                        :isUploading="isUploading"
                        :files="selectedFiles"
                        @remove-file="removeFile($event)"
                    ></common-upload-summary>

                    <common-warning v-if="showWarning" size="medium" :text="warningText"></common-warning>
                </div>
                <common-button
                    v-if="selectedFiles.length"
                    :label="$t('modals.patient_record.upload')"
                    type="primary"
                    size="large"
                    class="patient-details__upload-btn__send-files"
                    :isDisabled="(!selectedFiles.length || isSubmitDisabled) && !$auth.isUserPracticeDemo()"
                    :isProcessing="isUploading"
                    @click="sendFiles"
                ></common-button>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import dayjs from 'dayjs'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import UploadMixin from 'mixins/UploadMixin'
    import PatientAPI from 'api/PatientAPI'

    export default {
        mixins: [UploadMixin],
        props: {
            referral: { type: Object, required: true },
        },
        data() {
            return {
                userId: this.$auth.getUser().id,
                selectedFiles: [],
                isUploading: false,
                showWarning: false,
                warningText: '',
            }
        },
        computed: {
            patientFullName() {
                return this.referral.patient.firstName + ' ' + this.referral.patient.lastName
            },

            isSelfReferred() {
                return (
                    this.referral?.toUser?.id === this.referral?.fromUser?.id ||
                    this.referral?.practice?.id === this.referral?.fromPractice?.id
                )
            },

            appointmentDateTime() {
                return dayjs(this.referral.appointmentDateTime).format('MMM DD, YYYY')
            },

            referringPhysicianName() {
                if (this.$route.params.type === 'made') {
                    return this.referral.toUser ? composeDisplayName(this.referral.toUser) : this.referral?.practice?.name
                } else {
                    return this.referral.fromUser ? composeDisplayName(this.referral.fromUser) : this.referral?.fromPractice?.name
                }
            },

            referringPhysicianNameLabel() {
                const label =
                    this.referral.practice.practiceType === PracticeTypeEnum.ANCILLARY
                        ? 'common.referral_details.send_message_ancillary'
                        : 'common.referral_details.send_message'

                return this.$t(label, { name: this.referringPhysicianName })
            },
        },

        methods: {
            redirectToChatModal() {
                this.emitEvent({ name: 'close' })
                this.$router.push({
                    name: `referrals.${this.$route.params.type}.show`,
                    params: { id: this.referral.id },
                    query: { openChat: true },
                })
            },

            fileSelected(event) {
                this.onFileSelected(event)
                this.$refs.fileInput.value = ''
            },

            removeFile(file) {
                this.selectedFiles = this.selectedFiles.filter((f) => f !== file)
            },

            sendFiles() {
                this.isUploading = true
                this.showWarning = false
                this.showLimits = true
                Promise.all(
                    this.selectedFiles.map((file) => {
                        if (!file.isUploaded) return this.convertFileToBase64(file)
                        return Promise.resolve(file)
                    }),
                ).then((files) => {
                    this.selectedFiles = files
                    const filesForUpload = files.filter((file) => !file.isUploaded)
                    this.uploadFiles(filesForUpload)
                })
            },

            uploadFiles(files) {
                PatientAPI.uploadPatientRecord(this.referral.patient.id, this.$auth.getUser().id, files).then(() => {
                    if (this.errorFilesCount) {
                        this.warningText = this.$t('modals.patient_record.add.warnings.error', { n: this.errorFilesCount })
                        this.showWarning = true
                        this.showLimits = false
                        this.isUploading = false
                        return
                    }

                    this.sendPatientRecord()
                })
            },

            sendPatientRecord() {
                this.isUploading = false
                this.selectedFiles = []
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/page-elements/referrals/_referrals-patient-details-modal';
</style>
